import { Control, Controller, useController } from "react-hook-form";
import { Calendar, Select } from "@thetreep/ui";
import theme from "@theme";
import { TimeSelect } from "./TimeSelect";
import { DATE_TYPE_OPTIONS, SearchRequest } from "@services/travel";
import { getDateLocale } from "@utils/locale";
import { useTranslation } from "react-i18next";

export interface CalendarPanelProps {
  minDate?: Date;
  control: Control<SearchRequest>;
  name: "outwardDate" | "inwardDate";
  onSelectedNameChange?: (name: "outwardDate" | "inwardDate") => void;
}

export function CalendarPanel({
  minDate,
  control,
  name,
  onSelectedNameChange,
}: CalendarPanelProps) {
  const { t } = useTranslation();
  const { field: departureField } = useController({
    control,
    name: "outwardDate.date",
  });
  const { field: returnField } = useController({
    control,
    name: "inwardDate.date",
  });

  function getActiveDateField() {
    if (name == "outwardDate") {
      return departureField;
    } else {
      return returnField;
    }
  }

  return (
    <div
      className="
            flex flex-col items-center justify-center w-fit
            border border-grey-blue-300 rounded-xl px-4 bg-neutral-50"
    >
      <Calendar
        value={[departureField.value, returnField.value]}
        density="high"
        locale={getDateLocale()}
        onChange={(e) => {
          if (e?.date && (e.date as unknown as Date[]).length === 1) {
            onSelectedNameChange?.("outwardDate");
          } else if (e?.date && (e.date as unknown as Date[]).length === 2) {
            onSelectedNameChange?.("inwardDate");
          }

          const d = (e.date as unknown as Date[])[0];
          let departure = departureField.value;
          if (d) {
            const value = departureField.value;
            departure = new Date(
              d.getFullYear(),
              d.getMonth(),
              d.getDate(),
              value?.getHours() || 5,
              value?.getMinutes() || 0,
            );
            departureField.onChange(departure);
          }
          const r = (e.date as unknown as Date[])[1];
          if (r) {
            const value = returnField.value;
            // par defaut: depart + 1h si retour le meme jour, sinon 5h
            const fallback =
              departure && r.toDateString() === departure.toDateString()
                ? new Date(
                    Math.min(
                      departure.getTime() + 3600_000,
                      new Date(
                        r.getFullYear(),
                        r.getMonth(),
                        r.getDate(),
                        23,
                        30,
                      ).getTime(),
                    ),
                  )
                : new Date(r.getFullYear(), r.getMonth(), r.getDate(), 5, 0);
            returnField.onChange(
              value
                ? new Date(
                    r.getFullYear(),
                    r.getMonth(),
                    r.getDate(),
                    value.getHours(),
                    value.getMinutes(),
                  )
                : fallback,
            );
          }
        }}
        minDate={minDate}
        range
        overrides={{
          Day: {
            style: ({
              $selected,
              $pseudoSelected,
              $pseudoHighlighted,
              $isHovered,
            }) => ({
              ...($selected && {
                ":after": {
                  borderTopColor: theme.colors.cnam.blue.DEFAULT,
                  borderBottomColor: theme.colors.cnam.blue.DEFAULT,
                  borderRightColor: theme.colors.cnam.blue.DEFAULT,
                  borderLeftColor: theme.colors.cnam.blue.DEFAULT,
                  backgroundColor: theme.colors.cnam.blue.DEFAULT,
                },
              }),
              ...(($pseudoSelected ||
                $selected ||
                $pseudoHighlighted ||
                $isHovered) && {
                ":before": {
                  backgroundColor: theme.colors["light-blue"][200],
                },
              }),
            }),
          },
          MonthYearSelectButton: {
            props: {
              "data-testid": "search-date-select",
            },
          },
        }}
      />
      <div className="flex justify-between w-full gap-6 mb-4 [&_select]:p-2">
        <Controller
          key={name}
          name={`${name}.type`}
          control={control}
          render={({ field }) => (
            <Select
              options={DATE_TYPE_OPTIONS.map(({ labelKey, value }) => ({
                label: t(labelKey),
                value,
              }))}
              value={field.value ?? "departure"}
              onChange={field.onChange}
            />
          )}
        />
        <TimeSelect
          disabled={!getActiveDateField().value}
          control={control}
          name={name}
        />
      </div>
    </div>
  );
}
